import { useState } from 'react';
import { ApiError } from '@/api/client';
import { useBitriseApps, useProviderAccounts } from '@/api/hooks';
import type { BitriseApp, ProviderAccount } from '@/api/schemasProvider';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { accountLabel, bitriseAccountsOf, pickAccount, pickerHint, slugFallbackHint, slugFallbackOf, type SlugFallback } from './bitriseFieldModel';

interface Props {
  /** `bitriseAppSlug` ('' = not linked). */
  value: string;
  onChange: (slug: string) => void;
  error?: string;
  /** Prefix of the field ids (`app`, `edit-app`). */
  idPrefix: string;
}

type AppsQuery = ReturnType<typeof useBitriseApps>;

/** The API does not list Bitrise apps (route not served: 404/501). */
const listingUnsupported = (error: unknown) => error instanceof ApiError && (error.status === 404 || error.status === 501);

/** The slug as text (still a valid PATCH/POST field), when there is no account to list from or no listing on this API. */
function BitriseSlugInput({ why, value, onChange, error, idPrefix }: Props & { why: SlugFallback }) {
  const id = `${idPrefix}-bitriseAppSlug`;
  return (
    <FormField id={id} label="Bitrise app" hint={slugFallbackHint(why)} error={error}>
      <Input data-testid={`${idPrefix}-bitrise-app-slug`} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder="a1b2c3d4e5f6a7b8" spellCheck={false} maxLength={100} className="font-mono sm:text-[13px]" {...fieldA11y(id, error, 'hint')} />
    </FormField>
  );
}

interface AccountSelectProps {
  idPrefix: string;
  accounts: ProviderAccount[];
  account: ProviderAccount;
  onPick: (accountId: string) => void;
}

function BitriseAccountSelect({ idPrefix, accounts, account, onPick }: AccountSelectProps) {
  return (
    <FormField id={`${idPrefix}-bitrise-account`} label="Bitrise account">
      <OptionSelect id={`${idPrefix}-bitrise-account`} value={account.id} onValueChange={onPick} options={accounts.map((a) => ({ value: a.id, label: a.name }))} />
    </FormField>
  );
}

function BitriseAppsLoading({ id, workspace }: { id: string; workspace: string }) {
  return (
    <FormField id={id} label="Bitrise app" hint={`Loading the apps of ${workspace}…`}>
      <OptionSelect id={id} disabled value="" onValueChange={() => undefined} placeholder="Loading…" options={[]} />
    </FormField>
  );
}

/** The workspace has no app yet: Create on Bitrise makes one (SPEC decision 19), and a way to reload the list. */
function BitriseAppsEmpty({ idPrefix, workspace, apps }: { idPrefix: string; workspace: string; apps: AppsQuery }) {
  return (
    <div data-testid={`${idPrefix}-bitrise-app-empty`} className="flex flex-col gap-2 rounded-lg border border-dashed px-3 py-3 text-[13px]">
      <p className="font-medium text-foreground">No app in the Bitrise workspace {workspace} yet.</p>
      <p className="text-muted-foreground">Save this app, then press Create on Bitrise on its page: Hangar creates the Bitrise app and its workflows. Nothing to do on bitrise.io.</p>
      <Button type="button" size="sm" variant="outline" className="w-fit" loading={apps.isFetching} onClick={() => void apps.refetch()}>
        <Icon name="refresh" size={16} />
        Reload the list
      </Button>
    </div>
  );
}

interface SelectProps extends Props {
  id: string;
  list: BitriseApp[];
  workspace: string;
}

/** "Not linked", the saved slug when this workspace does not list it, then the workspace's apps. */
function bitriseAppOptions(list: BitriseApp[], value: string, listed: boolean) {
  const stale = value && !listed ? [{ value, label: `${value} (not in this workspace)` }] : [];
  return [{ value: '', label: 'Not linked' }, ...stale, ...list.map((a) => ({ value: a.slug, label: `${a.title} · ${a.slug}` }))];
}

function BitriseAppSelect({ id, idPrefix, list, workspace, value, onChange, error }: SelectProps) {
  const selected = list.find((a) => a.slug === value);
  return (
    <FormField id={id} label="Bitrise app" hint={pickerHint(selected, list.length, workspace)} error={error}>
      <OptionSelect data-testid={`${idPrefix}-bitrise-app-select`} value={value} onValueChange={onChange} options={bitriseAppOptions(list, value, Boolean(selected))} {...fieldA11y(id, error, 'hint')} />
    </FormField>
  );
}

/** Loading, empty workspace or the picker. */
function BitriseAppsBody({ apps, workspace, ...p }: Props & { apps: AppsQuery; workspace: string }) {
  const id = `${p.idPrefix}-bitriseAppSlug`;
  const list = apps.data ?? [];
  if (apps.isLoading && !apps.data) return <BitriseAppsLoading id={id} workspace={workspace} />;
  if (list.length === 0) return <BitriseAppsEmpty idPrefix={p.idPrefix} workspace={workspace} apps={apps} />;
  return <BitriseAppSelect {...p} id={id} list={list} workspace={workspace} />;
}

/**
 * The Bitrise app a native app builds on (`bitriseAppSlug`): picked from the workspace of the org's Bitrise account
 * (`GET /provider-accounts/{id}/bitrise-apps`), with the bitrise.io steps when the workspace has none. When the API does
 * not list apps (404/501) or there is no Bitrise account, the slug is typed instead.
 */
export function BitriseAppField(props: Props) {
  const accounts = useProviderAccounts();
  const bitriseAccounts = bitriseAccountsOf(accounts.data);
  const [accountId, setAccountId] = useState<string | undefined>();
  const account = pickAccount(bitriseAccounts, accountId);
  const apps = useBitriseApps(account?.id);
  const why = slugFallbackOf(Boolean(account), listingUnsupported(apps.error), apps.isError && !apps.data);
  if (!account || why) return <BitriseSlugInput {...props} why={why ?? 'no-account'} />;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid={`${props.idPrefix}-bitrise-app`}>
      {bitriseAccounts.length > 1 ? <BitriseAccountSelect idPrefix={props.idPrefix} accounts={bitriseAccounts} account={account} onPick={setAccountId} /> : null}
      <BitriseAppsBody {...props} apps={apps} workspace={accountLabel(account)} />
    </div>
  );
}
